interface SessionState {
  lastActiveColony?: string;
  lastActiveRoute?: string;
  lastActiveTimestamp?: string;
  lastKnowledgeColony?: string;
  userPreferences?: {
    preferredEntryPoint?: "landing" | "control-room";
    theme?: string;
    [key: string]: any;
  };
}

class SessionPersistence {
  private static instance: SessionPersistence;
  private readonly storageKey = 'braincolony-session';

  public static getInstance(): SessionPersistence {
    if (!SessionPersistence.instance) {
      SessionPersistence.instance = new SessionPersistence();
    }
    return SessionPersistence.instance;
  }

  private getSessionState(): SessionState {
    try {
      const stored = localStorage.getItem(this.storageKey);
      return stored ? JSON.parse(stored) : {};
    } catch (error) {
      console.warn('Error loading session state:', error);
      return {};
    }
  }

  private setSessionState(updates: Partial<SessionState>): void {
    try {
      const current = this.getSessionState();
      const newState = {
        ...current,
        ...updates,
        lastActiveTimestamp: new Date().toISOString()
      };
      localStorage.setItem(this.storageKey, JSON.stringify(newState));
    } catch (error) {
      console.warn('Error saving session state:', error);
    }
  }

  public setLastActive(colonyId: string, route: string): void {
    this.setSessionState({
      lastActiveColony: colonyId,
      lastActiveRoute: route
    });
  }

  public getLastActiveColony(): string | undefined {
    return this.getSessionState().lastActiveColony;
  }

  public getLastActiveRoute(): string | undefined {
    return this.getSessionState().lastActiveRoute;
  }

  public setPreference(key: string, value: any): void {
    const current = this.getSessionState();
    this.setSessionState({
      userPreferences: {
        ...current.userPreferences,
        [key]: value
      }
    });
  }

  public getPreference(key: string, defaultValue?: any): any {
    const preferences = this.getSessionState().userPreferences;
    return preferences?.[key] ?? defaultValue;
  }

  public shouldRestoreSession(): boolean {
    const state = this.getSessionState();
    if (!state.lastActiveTimestamp) return false;

    // Only restore if last activity was within 24 hours
    const lastActive = new Date(state.lastActiveTimestamp);
    const now = new Date();
    const hoursSince = (now.getTime() - lastActive.getTime()) / (1000 * 60 * 60);
    
    return hoursSince < 24;
  }

  public getRestoreRoute(): string | undefined {
    if (this.shouldRestoreSession()) {
      return this.getLastActiveRoute();
    }
    return undefined;
  }

  public setLastKnowledgeColony(colonyId: string): void {
    this.setSessionState({ lastKnowledgeColony: colonyId });
  }

  public getLastKnowledgeColony(): string | undefined {
    return this.getSessionState().lastKnowledgeColony;
  }

  public clearSession(): void {
    localStorage.removeItem(this.storageKey);
  }
}

export const sessionPersistence = SessionPersistence.getInstance();